import React, { useEffect, useRef, useState, useMemo } from 'react'
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
  TextField,
  Tooltip,
  Box,
  CircularProgress
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import filtersConfig from '../Filters'
import { MultiSelect } from 'react-multi-select-component';
import { ClearFilter, FilterIcon } from '../../../assets/images';
import { deleteEmergencyContactMapping, getAllEmergencyContactList, getAllEmergencyContactMappings, getAllFilters, getAllZoneList, updateBulkEmergencyContact } from '../../../services/configurationServices';
import AlertTemplateMessage from '../../Configuration/AlertTemplate/AlertTemplateMessage';
import { SuccessToast } from '../../../CustomHooks/SuccessToast';
import { ErrorToast } from '../../../CustomHooks/ErrorToast';
import Swal from 'sweetalert2';


const EmergencyContactMapping = () => {
    const [checked, setChecked] = useState([]);
    const [checkedContact, setContactChecked] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [emergencyContactSearchText, setEmergencyContactSearchText] = useState('');
    const [filterData, setFilterData] = useState(null);
    const [filterValues, setFilterValues] = useState({});
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedZone, setSelectedZone] = useState(null)
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [rowSelection, setRowSelection] = useState({});
    // Replace single loading state with granular loading states
    const [loadingZones, setLoadingZones] = useState(false);
    const [loadingMappings, setLoadingMappings] = useState(false);
    const [loadingContacts, setLoadingContacts] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false); // add/delete actions
    const [selectedId, setSelectedId] = useState(null);
    const [zoneData, setZoneData] = useState([]);
    const [contactData, setContactData] = useState([]);
    const [mappingData, setMappingData] = useState([]);

    
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    
    const wrapperRef = useRef(null);

    // Filter zones based on search text and sort in ascending order
    const filteredList = zoneData
        .filter(item => item.ZoneName?.toLowerCase().includes(searchText.toLowerCase()))
        .sort((a, b) => a.ZoneName?.localeCompare(b.ZoneName, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
        }));

    // Filter emergency contacts based on search text
    const filteredContactList = contactData
        .filter(item => item.ContactName?.toLowerCase().includes(emergencyContactSearchText.toLowerCase()))
        .sort((a, b) => a.ContactName?.localeCompare(b.ContactName, undefined, {
            numeric: true,
            sensitivity: 'base'
        }));

    const allContactChecked = filteredContactList.length > 0 && filteredContactList.every(item => checkedContact.includes(item.EmergencyContactID));
    const someContactChecked = filteredContactList.some(item => checkedContact.includes(item.EmergencyContactID)) && !allContactChecked;

    const handleToggle = (id) => async () => {
        setSelectedId(id);
        // Clear previous selections for contacts when switching zones
        setContactChecked([]);
        try {
            setLoadingContacts(true);
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const emergencyContactApiParams = {
                UserID: user.UserID || '',
                ClientID: '',
                ZoneID: id,
                Flag: 2
            };
            const customerVal = filterValues['Customer']?.[0]?.value;
            if (customerVal) emergencyContactApiParams.ClientID = customerVal;
            const contactResponse = await getAllEmergencyContactList(emergencyContactApiParams);
            if (contactResponse && contactResponse.Status === 'success' && contactResponse.Data) {
                setContactData(contactResponse.Data);
            } else {
                setContactData([]);
            }
        } catch (err) {
            console.error('Error loading emergency contacts on zone select:', err);
            setContactData([]);
        } finally {
            setLoadingContacts(false);
        }
    };

    const handleContactToggle = (id) => () => {
        setContactChecked(prev => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
    };

    const handleContactToggleAll = () => {
        if (allContactChecked) {
            setContactChecked(prev => prev.filter(id => !filteredContactList.some(item => item.EmergencyContactID === id)));
        } else {
            const newIds = filteredContactList.map(item => item.EmergencyContactID);
            setContactChecked(prev => [...new Set([...prev, ...newIds])]);
        }
    };

    const columns = useMemo(() => {
        // Dynamic columns based on mapping data
        const dynamicColumns = [];
        
        if (mappingData.length > 0) {
            const sampleData = mappingData[0];
            
            // Define column mappings for mapping data
            const columnMappings = {
                'ZoneName': { header: 'Zone Name', size: 350 },
                'ContactName': { header: 'Emergency Contact Name', size: 350 },
                'EmailID': { header: 'Email', size: 300 },
                'Phone': { header: 'Mobile No', size: 200 },
                'SiteName': { header: 'Site Name', size: 240 },
                'ContactLevel': { header: 'Level', size: 150 },
                'CreatedOn': { header: 'Created On', size: 150, isDate: true },
                'ModifiedOn': { header: 'Modified On', size: 150, isDate: true }
            };

            // Skip certain fields that shouldn't be displayed as columns
            const skipFields = ['CategoryID', 'OrganizationMasterID', 'SiteID', 'ZoneID',
                'Email', 'InApp', 'IsCall', 'IsCustomerSPOC', 'IsERT', 'WhatsApp', 'SMS', 'EmergencyContactID',
                'ModifiedBy', 'CreatedBy', 'UpdatedateTime', 'UserID', 'SubscriptionArn', 'Designation', 'ClientId', 'MappingID'
            ];

            // Create columns in the order defined by columnMappings
            Object.keys(columnMappings).forEach(key => {
                // Only add column if the field exists in the data and is not in skip list
                if (sampleData.hasOwnProperty(key) && !skipFields.includes(key)) {
                    const mapping = columnMappings[key];
                    const column = {
                        accessorKey: key,
                        header: mapping?.header || key.replace(/([A-Z])/g, ' $1').trim(),
                        size: mapping?.size || 150,
                        filterVariant: 'text',
                    };

                    // Special formatting for date columns
                    if (mapping?.isDate) {
                        column.Cell = ({ cell }) => {
                            const value = cell.getValue();
                            if (!value) return '-';
                            try {
                                return new Date(value).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                });
                            } catch {
                                return value;
                            }
                        };
                    }

                    dynamicColumns.push(column);
                }
            });

            // Add any remaining fields from sampleData that aren't in columnMappings (fallback)
            Object.keys(sampleData).forEach(key => {
                if (!skipFields.includes(key) && key !== 'actions' && !columnMappings[key]) {
                    dynamicColumns.push({
                        accessorKey: key,
                        header: key.replace(/([A-Z])/g, ' $1').trim(),
                        size: 150,
                        filterVariant: 'text',
                    });
                }
            });
        }

        // Always add actions column at the end
        dynamicColumns.push({
            accessorKey: "actions",
            header: "Actions",
            enableColumnActions: false,
            enableSorting: false,
            Cell: ({ row }) => (
                <div className="ActionBtns">
                    <Tooltip title="Delete Mapping" placement="bottom">
                        <button 
                            className="DataAccessEdit borderButton" 
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMapping(row.original);
                            }}
                            disabled={loadingAction}
                            style={{
                                opacity: loadingAction ? 0.5 : 1,
                                cursor: loadingAction ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <DeleteForeverIcon className="ActionsIcon" />
                        </button>
                    </Tooltip>
                </div>
            ),
        });

        return dynamicColumns;
    }, [mappingData]);

  const handleDeleteMapping = async (mapping) => {
    console.log('Delete mapping:', mapping);
      Swal.fire({
          title: "Are you sure?",
          text: `You really want to delete the mapping for "${mapping.ContactName}" in zone "${mapping.ZoneName}"!`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
          allowOutsideClick: false,
      }).then(async (result) => {
          if (result.isConfirmed) {
              try {
                  setLoadingAction(true);
                  
                  // Get user ID from localStorage
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  const userID = user.Id 
                  
                  const response = await deleteEmergencyContactMapping({
                      UserID: userID,
                      EmergencyContactID: mapping.EmergencyContactID,
                      ZoneID: mapping.ZoneID
                  });
                  
                  console.log('Delete mapping response:', response);
                  
                  if (response && response.Status === 'success') {
                      SuccessToast(response.Message || 'Emergency contact mapping deleted successfully!');
                      // Refresh the mapping data after successful deletion
                      await handleApplyFilters();
                  } else {
                      ErrorToast(response?.Message || 'Failed to delete emergency contact mapping');
                  }
              } catch (error) {
                  console.error('Error deleting mapping:', error);
                  ErrorToast('An error occurred while deleting the emergency contact mapping');
              } finally {
                  setLoadingAction(false);
              }
          }
      });
  };

  const handleUpdateBulkEmergencyContact = async (updatedData) => {
      try {
          setLoadingAction(true);
          const response = await updateBulkEmergencyContact(updatedData);
          console.log('Bulk emergency contact updated successfully:', response);
          
          if (response && response.Status === 'success') {
              // Success handling - refresh the mapping data
              await handleApplyFilters(); // Refresh the data after successful mapping
              
              // Clear selections after successful mapping
              setSelectedId(null);
              setContactChecked([]);
              
              // Show success message (you can replace with your toast notification)
              SuccessToast('Emergency contact mapping added successfully!')
          } else {
              // Handle error response
              ErrorToast(response?.Message || 'Failed to add emergency contact mapping');
              //alert(response?.Message || 'Failed to add emergency contact mapping');
          }
      } catch (error) {
          console.error('Error updating bulk emergency contact:', error);
          ErrorToast('An error occurred while adding the emergency contact mapping');
      } finally {
          setLoadingAction(false);
      }
  };

  const handleAddMapping = async () => {
      if (!selectedId || checkedContact.length === 0) {
          alert('Please select a zone and at least one emergency contact');
          return;
      }

      // Get user ID from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userID = user.Id || user.UserID || '';

      if (!userID) {
          alert('User not found. Please log in again.');
          return;
      }

      // Prepare the payload
      const payload = {
          UserID: userID,
          ContactID: checkedContact, // Array of selected emergency contact IDs
          ZoneID: selectedId // Selected zone ID
      };

      console.log('Add Mapping Payload:', payload);
      
      // Call the API
      await handleUpdateBulkEmergencyContact(payload);
  };

    const pagedData = useMemo(() => {
        const start = pagination.pageIndex * pagination.pageSize;
        const end = start + pagination.pageSize;
        return mappingData.slice(start, end);
    }, [mappingData, pagination]);

    // Enhanced configuration with extensible field mappings
        const getPageConfiguration = () => {
            const pageConfig = filtersConfig.find(config => config.pagename === 'EmergencyContactMappingConfiguration');
            if (!pageConfig) {
                console.warn('EmergencyContactConfiguration not found in filters config');
                return { filters: {}, fieldMapping: {}, displayConfig: {} };
            }
            return pageConfig;
        };
    
        const pageConfig = getPageConfiguration();
        const pageFilters = pageConfig.filters;
        
        // Dynamic field mapping configuration - can be moved to external config
        const defaultFieldMapping = {
            'Customer': 'ClientName',
            'State': 'statename', 
            'City': 'City',
            'Site': 'SiteName',
            'Zone': 'ZoneName',
            'Interface': 'InterfaceName',
            'Device': 'DeviceName'
        };
    
        // Use configured field mapping or fall back to default
        const fieldMapping = pageConfig.fieldMapping || defaultFieldMapping;
    
        // Get the actual field name from configuration (with fallback to default mapping)
        const getFieldName = (filterKey) => {
            const filterConfig = pageFilters[filterKey];
            if (filterConfig && filterConfig.fieldName) {
                return filterConfig.fieldName;
            }
            return fieldMapping[filterKey] || filterKey;
        };
    
        // Get placeholder text from configuration
        const getPlaceholderText = (filterKey, filterConfig) => {
            if (filterConfig.placeholder) {
                return filterConfig.placeholder;
            }
            return `Select ${filterConfig.label}${filterConfig.mandatory ? '*' : ''}`;
        };
        
        // Excel-like Filter Utility - filters options based on other active selections
        const getFilteredOptions = (filterKey, optionKey, labelKey) => {
            if (!filterData || !filterData.Data) {
                return [];
            }
    
            // Build a filter object with only selected values for each filter (excluding current filter)
            const activeSelections = {};
            Object.keys(pageFilters).forEach(key => {
                const selected = filterValues[key];
                if (
                    key !== filterKey &&
                    Array.isArray(selected) &&
                    selected.length > 0
                ) {
                    activeSelections[key] = selected.map(item => 
                        typeof item === "object" ? item.value : item
                    );
                }
            });
    
            // Filter data based on all other active selections
            const filtered = filterData.Data.filter(item => {
                return Object.entries(activeSelections).every(([key, selectedVals]) => {
                    // Use the same field mapping logic as in getFilterOptions
                    let fieldName;
                    if (key === 'Customer') {
                        fieldName = 'ClientId';
                    } else if (key === 'State') {
                        fieldName = 'statemasterid';
                    } else if (key === 'City') {
                        fieldName = 'City';
                    } else if (key === 'Site') {
                        fieldName = 'SiteID';
                    } else if (key === 'Zone') {
                        fieldName = 'ZoneID';
                    } else {
                        fieldName = getFieldName(key);
                    }
                    return selectedVals.includes(item[fieldName]);
                });
            });
    
            // Get unique options for this filter
            const seen = new Set();
            return filtered
                .filter(item => {
                    const val = item[optionKey];
                    if (!val || seen.has(val)) return false;
                    seen.add(val);
                    return true;
                })
                .map(item => ({
                    label: item[labelKey],
                    value: item[optionKey],
                }))
                .sort((a, b) => {
                    // Sort options in ascending order by label
                    return a.label.localeCompare(b.label, undefined, { 
                        numeric: true, 
                        sensitivity: 'base' 
                    });
                });
        };
         // Create options from API filterData with Excel-like filtering - now fully dynamic
    const getFilterOptions = (filterKey) => {
        if (!filterData || !filterData.Data) {
            return [];
        }
        
        let optionKey, labelKey;
        
        if (filterKey === 'Customer') {
            // For Customer, use ClientId as value and ClientName as label
            optionKey = 'ClientId';
            labelKey = 'ClientName';
        } else if (filterKey === 'State') {
            // For State, use statemasterid as value and statename as label
            optionKey = 'statemasterid';
            labelKey = 'statename';
        } else if (filterKey === 'City') {
            // For City, use City as both value and label (no separate ID in your data)
            optionKey = 'City';
            labelKey = 'City';
        } else if (filterKey === 'Site') {
            // For Site, use SiteID as value and SiteName as label
            optionKey = 'SiteID';
            labelKey = 'SiteName';
        } else if (filterKey === 'Zone') {
            // For Zone, use ZoneID as value and ZoneName as label
            optionKey = 'ZoneID';
            labelKey = 'ZoneName';
        } else {
            // Fallback to field name for both (existing behavior)
            const fieldName = getFieldName(filterKey);
            optionKey = fieldName;
            labelKey = fieldName;
        }
        
        return getFilteredOptions(filterKey, optionKey, labelKey);
    };

    const handleFilterChange = (filterKey, value) => {
        console.log("FilterKey", filterKey)
        console.log("FilterKey", value)
        if(filterKey == 'Customer' && value.length > 0) {
            console.log("FilterKey", value)
            setSelectedCustomer(value[0])
        }
        if(filterKey == "Zone" && value.length > 0){
            setSelectedZone(value[0])
        }
        setFilterValues(prev => {
            const newValues = {
                ...prev,
                [filterKey]: value
            };

            // Smart dependency clearing - clear dependent filters when parent changes
            const filterConfig = pageFilters[filterKey];
            if (filterConfig) {
                // Find and clear all dependent filters
                Object.entries(pageFilters).forEach(([key, config]) => {
                    if (config.parentFilter === filterKey) {
                        newValues[key] = [];
                        // Recursively clear dependencies
                        const clearDependencies = (parentKey) => {
                            Object.entries(pageFilters).forEach(([depKey, depConfig]) => {
                                if (depConfig.parentFilter === parentKey) {
                                    newValues[depKey] = [];
                                    clearDependencies(depKey);
                                }
                            });
                        };
                        clearDependencies(key);
                    }
                });
            }

            console.log("FilterKey", newValues)
            return newValues;
        });

        // Only reset table state when mandatory filters are changed
        const filterConfig = pageFilters[filterKey];
        if (filterConfig && filterConfig.mandatory) {
            setContactData([]);
            setZoneData([]);
            setMappingData([]);
            setFiltersApplied(false);
            setRowSelection({});
            setChecked([]);
            setContactChecked([]);
            setSelectedId(null);
            setPagination({
                pageIndex: 0,
                pageSize: 10,
            });
        }
    };

        // Enhanced validation with better error messaging
        const isMandatoryFiltersMissing = () => {
            return Object.entries(pageFilters).some(([filterKey, filterConfig]) => {
                return filterConfig.mandatory && (!filterValues[filterKey] || filterValues[filterKey].length === 0);
            });
        };

        // Get missing mandatory filters for better user feedback
        const getMissingMandatoryFilters = () => {
            return Object.entries(pageFilters)
                .filter(([filterKey, filterConfig]) => {
                    return filterConfig.mandatory && (!filterValues[filterKey] || filterValues[filterKey].length === 0);
                })
                .map(([filterKey, filterConfig]) => filterConfig.label);
        };

        // Enhanced filter button tooltip
        const getFilterButtonTooltip = () => {
            const missingFilters = getMissingMandatoryFilters();
            if (missingFilters.length > 0) {
                return `Please select: ${missingFilters.join(', ')}`;
            }
            return "Apply Filter";
        };

        // Clear all filters function
    const handleClearAllFilters = () => {
        setFilterValues({});
        setContactData([]);
        setZoneData([]);
        setMappingData([]);
        setFiltersApplied(false);
        setRowSelection({});
        setChecked([]);
        setContactChecked([]);
        setSelectedId(null);
        setPagination({
            pageIndex: 0,
            pageSize: 10,
        });
    };

    // Apply filters function - now calls API and stores response
    const handleApplyFilters = async () => {
        if (!isMandatoryFiltersMissing()) {
            setFiltersApplied(true);
            setContactData([]); // keep empty until zone click
            try {
                setLoadingZones(true);
                setLoadingMappings(true);
                const user = JSON.parse(localStorage.getItem('user') || '{}');
                const apiParams = { UserID: user.UserID || '', ClientID: '', StateID: '', CityID: '', SiteID: '', ZoneID: '' };
                Object.entries(filterValues).forEach(([filterKey, values]) => {
                    if (Array.isArray(values) && values.length > 0) {
                        const value = values[0].value;
                        if (filterKey === 'Customer') apiParams.ClientID = value;
                        else if (filterKey === 'State') apiParams.StateID = value;
                        else if (filterKey === 'City') apiParams.CityID = value;
                        else if (filterKey === 'Site') apiParams.SiteID = value;
                        else if (filterKey === 'Zone') apiParams.ZoneID = value;
                    }
                });
                console.log('Emergency Contact Mapping (zones/mappings only) API Parameters:', apiParams);
                const [zoneResponse, mappingResponse] = await Promise.all([
                    getAllZoneList(apiParams).catch(e=>{console.warn('Zone fetch error', e); return null;}),
                    getAllEmergencyContactMappings(apiParams).catch(e=>{console.warn('Mapping fetch error', e); return null;})
                ]);
                if (zoneResponse && zoneResponse.Status === 'success' && zoneResponse.Data) {
                    setZoneData(zoneResponse.Data);
                } else {
                    setZoneData([]);
                }
                if (mappingResponse && mappingResponse.Status === 'success' && mappingResponse.Data) {
                    setMappingData(mappingResponse.Data);
                } else {
                    setMappingData([]);
                }
            } catch (error) {
                console.error('Error applying Emergency Contact Mapping filters:', error);
                setZoneData([]);
                setMappingData([]);
            } finally {
                setLoadingZones(false);
                setLoadingMappings(false);
            }
        }
    };

    // Fetch filters on component mount
    useEffect(() => {
        fetchFilters();
    }, []);

    const fetchFilters = async () => {
        try {
            const response = await getAllFilters({ Entity: 'EmergencyContact' });
            setFilterData(response);
            console.log('EmergencyContact filter data loaded:', response);
        } catch (error) {
            console.error('Error loading emergency contact filters:', error);
            setFilterData(null);
        }
    };

    useEffect(() => {
        if (wrapperRef.current) {
            const labelEl = wrapperRef.current.querySelector(
                ".dropdown-heading .dropdown-heading-value"
            );
            if (labelEl) {
                const firstFilterKey = Object.keys(pageFilters)[0];
                const firstSelection = filterValues[firstFilterKey];
                const selectedLabel = firstSelection?.[0]?.label || "";
                const isTruncated = labelEl.scrollWidth > labelEl.clientWidth;
                labelEl.setAttribute("title", isTruncated ? selectedLabel : "");
            }
        }
    }, [filterValues]);

    return (
        <div className='p-3'>
            <div className="SiteMainContainer SiteTableMainDiv">
                {/* <h5>Zone-Emergency Contact Mappings</h5> */}
                <div className="DashboardFilterMainDiv">
                    <div className="RefreshText" style={{ display: "flex" }}>
                        {Object.entries(pageFilters)
                            .sort(([, a], [, b]) => a.sequence - b.sequence)
                            .map(([filterKey, filterConfig]) => (
                                <div key={filterKey} className="inputTypeLabelContainer">
                                    <label className="formFieldLabels">
                                        {filterConfig.label}
                                        {filterConfig.mandatory && <span style={{ color: 'red' }}>*</span>}
                                    </label>
                                    <div ref={wrapperRef}>
                                        <MultiSelect
                                            key={filterValues[filterKey]?.[0]?.value}
                                            options={getFilterOptions(filterKey)}
                                            value={filterValues[filterKey] || []}
                                            onChange={(value) => handleFilterChange(filterKey, value.length > 1 ? [value[value.length - 1]] : value)}
                                            labelledBy={`Select ${filterConfig.label}`}
                                            hasSelectAll={false}
                                            isCreatable={false}
                                            disableSearch={false}
                                            className="SiteConfigSingleSelectDropdownContainer"
                                            overrideStrings={{
                                                selectSomeItems:
                                                    (filterValues[filterKey] && filterValues[filterKey][0] && filterValues[filterKey][0].label)
                                                        ? filterValues[filterKey][0].label
                                                        : getPlaceholderText(filterKey, filterConfig),
                                                allItemsAreSelected:
                                                    (filterValues[filterKey] && filterValues[filterKey][0] && filterValues[filterKey][0].label)
                                                        ? filterValues[filterKey][0].label
                                                        : getPlaceholderText(filterKey, filterConfig),
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        <div className="HMFilterDiv">
                            <Tooltip title={getFilterButtonTooltip()} placement="bottom">
                                <button
                                    className="IconsButtonDiv"
                                    name="filterBtn"
                                    disabled={isMandatoryFiltersMissing() || loadingZones || loadingMappings || loadingAction}
                                    style={{
                                        opacity: (isMandatoryFiltersMissing() || loadingZones || loadingMappings || loadingAction) ? 0.5 : 1,
                                        cursor: (isMandatoryFiltersMissing() || loadingZones || loadingMappings || loadingAction) ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={handleApplyFilters}
                                >
                                    <img
                                        src={FilterIcon}
                                        className="camera-icon-height"
                                        alt="Apply filter icon"
                                    />
                                </button>
                            </Tooltip>
                            <Tooltip title="Clear Filter" placement="bottom">
                                <button
                                    className="IconsButtonDiv"
                                    name="clearFilterBtn"
                                    disabled={loadingZones || loadingMappings || loadingAction}
                                    style={{
                                        opacity: loadingZones || loadingMappings || loadingAction ? 0.5 : 1,
                                        cursor: loadingZones || loadingMappings || loadingAction ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={handleClearAllFilters}
                                >
                                    <img
                                        src={ClearFilter}
                                        className="camera-icon-height"
                                        alt="Clear filter icon"
                                    />
                                </button>
                            </Tooltip>
                        </div>
                    </div>
                </div>
                <div className='SiteFormAdditionForm pt-2' style={{height:"calc(100vh - 235px)"}}>
                    <div className='ZoneContainerDiv'>
                        <div className='p-1' style={{ border: "1px solid #d3d3d3", width:"100%" }}>
                        <h6>Zone List</h6>
                            <>
                                <TextField
                                    placeholder="Search..."
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className='ZoneSearchDiv'
                                />
                                
                                <div style={{ height: "calc(100vh - 655px)", overflow: "auto" }}>
                                   <List className="pt-0" dense>
                                    {loadingZones ? (
                                        <ListItem>
                                            <ListItemText 
                                                primary="Loading zones..." 
                                                sx={{ fontStyle: 'italic', color: 'gray' }}
                                            />
                                        </ListItem>
                                    ) : filteredList.length > 0 ? filteredList.map((item) => (
                                        <ListItem
                                        key={item.ZoneID}
                                        dense
                                        button
                                        onClick={handleToggle(item.ZoneID)}
                                        selected={selectedId === item.ZoneID}
                                        className={selectedId === item.ZoneID ? 'ZoneListItemSelected' : 'ZoneListItem'}
                                        sx={{ cursor: 'pointer' }}
                                        >
                                        <ListItemText primary={item.ZoneName} />
                                        </ListItem>
                                    )) : (
                                        <ListItem>
                                            {/* <ListItemText 
                                                primary={filtersApplied ? "No zones found for the selected site" : "Please apply filters to load zones"} 
                                                sx={{ fontStyle: 'italic', color: 'gray' }}
                                            /> */}
                                            <div style={{ width: "100%", paddingTop: "10px" }}>
                                            <AlertTemplateMessage
                                                message={
                                                    filtersApplied
                                                        ? "No zones found for the selected site"
                                                        : "Please apply filters to load zones"
                                                }
                                            />
                                            </div>
                                        </ListItem>
                                    )}
                                    </List>
                                </div>
                            </>
                        </div>
                        <div className='p-1' style={{ border: "1px solid #d3d3d3", width:"100%"  }}>
                        <h6>Emergency Contact List</h6>
                            <>
                                <TextField
                                    placeholder="Search..."
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={emergencyContactSearchText}
                                    onChange={(e) => setEmergencyContactSearchText(e.target.value)}
                                    className='ZoneSearchDiv'
                                />
                                
                               <div style={{ height: "calc(100vh - 700px)", overflow: "auto" }}>
                                    <List className="pt-0" dense>
                                        {loadingContacts ? (
                                            <ListItem>
                                                <ListItemText primary="Loading emergency contacts..." sx={{ fontStyle: 'italic', color: 'gray' }} />
                                            </ListItem>
                                        ) : filteredContactList.length > 0 ? (
                                            <>
                                                {/* Select All only when contacts exist */}
                                                <ListItem className="p-0">
                                                    <Checkbox
                                                        indeterminate={someContactChecked}
                                                        checked={allContactChecked}
                                                        onChange={handleContactToggleAll}
                                                        disabled={loadingContacts || filteredContactList.length === 0}
                                                    />
                                                    <ListItemText
                                                        primary="Select All"
                                                        primaryTypographyProps={{ fontWeight: 'bold' }}
                                                    />
                                                </ListItem>
                                                <Divider />
                                                {filteredContactList.map((item) => (
                                                    <ListItem
                                                        key={item.EmergencyContactID}
                                                        dense
                                                        button
                                                        onClick={handleContactToggle(item.EmergencyContactID)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': { backgroundColor: '#e3f2fd' },
                                                        }}
                                                    >
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={checkedContact.includes(item.EmergencyContactID)}
                                                                tabIndex={-1}
                                                                disableRipple
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={item.ContactName}
                                                            secondary={item.EmailID || item.Phone}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </>
                                        ) : (
                                            <ListItem>
                                                <div style={{ width: "100%", paddingTop: "10px" }}>
                                                    <AlertTemplateMessage
                                                        message={
                                                            !selectedId
                                                                ? "Please select a zone from the zone list to load emergency contacts"
                                                                : "No Emergency Contact available for mapping."
                                                        }
                                                    />
                                                </div>
                                            </ListItem>
                                        )}
                                    </List>
                                </div>
                            </>
                        </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <button
                            className="DataAccessEdit mt-3"
                            style={{ 
                                width: "150px",
                                opacity: (selectedId && checkedContact.length > 0 && !loadingAction) ? 1 : 0.5,
                                cursor: (selectedId && checkedContact.length > 0 && !loadingAction) ? 'pointer' : 'not-allowed'
                            }}
                            disabled={!selectedId || checkedContact.length === 0 || loadingAction}
                            onClick={handleAddMapping}
                            title={
                                loadingAction ? 'Processing...' : !selectedId ? 'Please select a zone' : checkedContact.length === 0 ? 'Please select emergency contacts' : 'Add Mapping'
                            }
                        >
                            <span className="ViewUpdate">
                                {loadingAction ? 'Adding...' : 'Add Mapping'}
                            </span>
                        </button>
                    </div>
                    {/* {filtersApplied && emergencyContactData.length > 0 && (
                        <div className="mt-3 mb-3" style={{ textAlign: "center", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
                            <span style={{ color: "#666", fontSize: "14px" }}>
                                Showing {emergencyContactData.length} emergency contact{emergencyContactData.length !== 1 ? 's' : ''} 
                                {zoneData.length > 0 && ` and ${zoneData.length} zone${zoneData.length !== 1 ? 's' : ''}`}
                                {selectedCustomer && ` for ${selectedCustomer.label}`}
                            </span>
                        </div>
                    )} */}
                    <div className='mt-3' style={{width:"100%", height:"100%", padding:"0 70px"}}>
                        <div className="col-lg-12 m-0 TabulerReportMainDiv MatReactTableDiv" style={{width:"100%", height: "calc(100vh - 280px)"}}>
                            <div className="card-body p-0">
                                {loadingMappings ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" flexDirection="column">
                                        <CircularProgress size={60} thickness={4} />
                                        <Box mt={2}><span style={{ fontSize: '16px', color: '#666' }}>Loading emergency contact mapping data...</span></Box>
                                    </Box>
                                ) : mappingData.length === 0 ? (
                                    <div style={{ width: "100%", padding: "20px", textAlign: "center" }}>
                                        <AlertTemplateMessage
                                            message={
                                                !filtersApplied || isMandatoryFiltersMissing()
                                                    ? "Please select mandatory filters and click apply filter to load emergency contact mapping data"
                                                    : "No emergency contact mappings found for the selected filters"
                                            }
                                        />
                                    </div>
                                ) : (
                                    <MaterialReactTable
                                        columns={columns}
                                        data={pagedData}
                                        enableGlobalFilter={true}
                                        enableColumnFilters={false}
                                        enableStickyHeader
                                        enableColumnResizing={true}
                                        globalFilterFn="contains"
                                        columnResizeMode="onEnd"
                                        //enableColumnOrdering={true}
                                        //enableRowSelection={true}
                                        enableHiding={true}
                                        initialState={{ showColumnFilters: false }}
                                        manualPagination={true}
                                        rowCount={mappingData.length}
                                        getRowId={(row) => row.MappingID || row.EmergencyContactID || row.ID || `row-${Math.random()}`}
                                        state={{
                                            pagination: pagination,
                                            rowSelection: rowSelection,
                                        }}
                                        onRowSelectionChange={setRowSelection}
                                        onPaginationChange={setPagination}
                                        muiTableContainerProps={{
                                            className: "MaterialTableJs myTableContainer",
                                            sx: {
                                                maxHeight: "100%",
                                                overflow: "auto",
                                            },
                                        }}
                                        muiTableHeadProps={{
                                            className: "myHeader",
                                            sx: {
                                                display: "table-header-group",
                                            },
                                        }}
                                        renderTopToolbarCustomActions={({ table }) => (
                                            <div className='d-flex justify-content-center align-items-center'>
                                                <h6 style={{ margin: 0 }}>
                                                    Emergency Contact Mappings {mappingData.length > 0 && `(${mappingData.length} records)`}
                                                </h6>
                                            </div>
                                        )}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EmergencyContactMapping